import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { UsageChart } from "components/Analytics/UsageChart/UsageChart"
import { ProductDistributionChart } from "components/Analytics/ProductDistributionChart/ProductDistributionChart"
import { EnhancedAnalyticsSummary } from "components/Analytics/EnhancedAnalyticsSummary/EnhancedAnalyticsSummary"
import { TimeBasedComparison } from "components/Analytics/TimeBasedComparison/TimeBasedComparison"
import { ConversionFunnelChart } from "components/Analytics/ConversionFunnelChart/ConversionFunnelChart"
import { MetricsChart } from "components/Analytics/MetricsChart/MetricsChart"
import { useAnalyticsQuery } from "hooks/query/useAnalyticsQuery"
import { useMerchantsQuery } from "hooks/query/useMerchantQuery"
import { useAppSelector } from "hooks/storeHooks"
import { selectUserInfo } from "store/user/userSlice"
import { useCurrentId } from "hooks/useCurrentId"
import { MerchantSelector } from "components/MerchantSelector/MerchantSelector"
import { DateRangePicker } from "components/Analytics/DateRangePicker"
import { Download, Loader2, BarChart3, RefreshCw } from "lucide-react"
import { downloadFile } from "utils/fileReader"
import { useState, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"

export const AnalyticsPage = () => {
  const { t } = useTranslation()
  const userInfo = useAppSelector(selectUserInfo)
  const urlMerchantId = useCurrentId()

  const isAdmin = userInfo?.groups?.includes("Admin") || false

  const {
    data: merchants = [],
    isLoading: merchantsLoading,
    error: merchantsError,
  } = useMerchantsQuery(isAdmin)

  const [selectedMerchant, setSelectedMerchant] = useState("")
  const [selectedMerchantForDisplay, setSelectedMerchantForDisplay] = useState("")

  const today = dayjs().format("YYYY-MM-DD")
  const last30Days = dayjs().subtract(29, "day").format("YYYY-MM-DD")
  const [dateStart, setDateStart] = useState(last30Days)
  const [dateEnd, setDateEnd] = useState(today)

  const handleDateRangeChange = (start: string, end: string) => {
    setDateStart(start)
    setDateEnd(end)
  }

  useEffect(() => {
    if (!isAdmin && userInfo?.merchantId && !selectedMerchant)
      setSelectedMerchant(userInfo.merchantId)
  }, [isAdmin, userInfo?.merchantId, selectedMerchant])

  useEffect(() => {
    if (isAdmin && urlMerchantId) {
      setSelectedMerchant(urlMerchantId)
      const merchantInfo = merchants?.find((m: any) => m.id === urlMerchantId)
      if (merchantInfo)
        setSelectedMerchantForDisplay(`${merchantInfo.name} (${merchantInfo.domain})`)
    }
  }, [isAdmin, urlMerchantId, merchants])

  const {
    data: analyticsData,
    isLoading,
    error,
  } = useAnalyticsQuery({
    merchantId: selectedMerchant,
    dateStart,
    dateEnd,
  })

  const parsedData: any = analyticsData || null

  let widgetAnalytics = []
  let productAnalytics = []
  let dataState = { hasData: false, hasBillingCycle: false, message: "", title: "" }

  if (parsedData)
    if (Array.isArray(parsedData)) {
      const transformedData = parsedData.map((item) => ({
        date: item.date,
        hostname: item.hostname,
        unique_users: item.unique_users,
        engaged_users: item.engaged_users,
        app_opened: item.app_open_events,
        vto_events: item.vto_events,
        vto_users: item.vto_users,
        add_to_cart: item.add_to_cart_events,
        add_to_cart_users: item.add_to_cart_users,
        add_to_cart_total_skus: item.add_to_cart_total_skus,
        photo_upload_click: item.photo_uploaded_events,
        product_title: item.product_title,
        merchant_id: item.merchant_id,
        user_engagement_secs: item.user_engagement_secs,
      }))

      widgetAnalytics = transformedData
      productAnalytics = transformedData
      dataState = { hasData: parsedData.length > 0, hasBillingCycle: false, message: "", title: "" }
    } else if (parsedData.widgetAnalytics || parsedData.productAnalytics) {
      widgetAnalytics = parsedData.widgetAnalytics || []
      productAnalytics = parsedData.productAnalytics || []
      dataState = parsedData.dataState || {
        hasData: false,
        hasBillingCycle: false,
        message: "",
        title: "",
      }
    }

  const generateUsageDataFromProducts = () => {
    const start = dayjs(dateStart)
    const end = dayjs(dateEnd)
    const daysDiff = end.diff(start, "day") + 1

    const dateRange = Array.from({ length: daysDiff }, (_, i) =>
      start.add(i, "day").format("YYYY-MM-DD")
    )

    let cumulativeUsage = 0
    const usageData = dateRange.map((date) => {
      const dailyUsage = productAnalytics
        .filter((product: any) => product.date === date)
        .reduce((sum: number, product: any) => sum + product.vto_events, 0)

      cumulativeUsage += dailyUsage

      return {
        date: dayjs(date).format("MMM D"),
        usage: cumulativeUsage,
        extrapolatedUsage: null,
      }
    })

    return usageData
  }

  const usageData = generateUsageDataFromProducts()
  const totalUsage = usageData[usageData.length - 1]?.usage || 0

  const selectedMerchantInfo = merchants?.find((m: any) => m.id === selectedMerchant)

  const exportToCSV = () => {
    if (!widgetAnalytics.length && !productAnalytics.length) return

    const headers = [
      "Date",
      "Hostname",
      "Product Title",
      "Unique Users",
      "Engaged Users",
      "App Opened",
      "VTO Events",
      "VTO Users",
      "Add to Cart Events",
      "Add to Cart Users",
      "Add to Cart Total SKUs",
      "Photo Upload Events",
      "User Engagement Seconds",
      "Merchant ID",
    ]

    const rows = widgetAnalytics.map((item: any) => [
      item.date || "",
      item.hostname || "",
      item.product_title || "",
      item.unique_users || 0,
      item.engaged_users || 0,
      item.app_opened || 0,
      item.vto_events || 0,
      item.vto_users || 0,
      item.add_to_cart || 0,
      item.add_to_cart_users || 0,
      item.add_to_cart_total_skus || 0,
      item.photo_upload_click || 0,
      item.user_engagement_secs || 0,
      item.merchant_id || "",
    ])

    const csvContent = [
      headers.join(","),
      ...rows.map((row: any[]) => row.map((cell: any) => `"${cell}"`).join(",")),
    ].join("\n")

    const filename = `analytics_${dateStart}_to_${dateEnd}_${dayjs().format(
      "YYYY-MM-DD_HH-mm-ss"
    )}.csv`
    downloadFile(csvContent, filename, "text/csv;charset=utf-8;")
  }

  const handleMerchantSelect = (merchantDisplay: string) => {
    setSelectedMerchantForDisplay(merchantDisplay)

    if (merchantDisplay.startsWith("Merchant ID: ")) {
      const merchantId = merchantDisplay.replace("Merchant ID: ", "")
      setSelectedMerchant(merchantId)
      return
    }

    const merchant = merchants?.find((m: any) => `${m.name} (${m.domain})` === merchantDisplay)
    if (merchant) setSelectedMerchant(merchant.id)
  }

  const hasData = selectedMerchant && (widgetAnalytics.length > 0 || productAnalytics.length > 0)
  const showLoading = isLoading || (isAdmin && merchantsLoading)
  const showError = error || (isAdmin && merchantsError)
  const showEmpty =
    !showLoading &&
    !showError &&
    !dataState.hasData &&
    (!parsedData || (Array.isArray(parsedData) && parsedData.length === 0))

  return (
    <div className="p-6" data-testid="analytics-page">
      <div className="mx-auto max-w-[1440px] space-y-3">
        {/* ──── Page header ──── */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="analytics-title">
                {t("analytics.dashboard.title")}
              </h1>
              <p className="text-sm text-muted-foreground">{t("analytics.dashboard.subtitle")}</p>
            </div>

            {(isAdmin || selectedMerchantInfo) && (
              <div className="flex items-center gap-3">
                {isAdmin && (
                  <MerchantSelector
                    merchants={merchants || []}
                    onMerchantSelect={handleMerchantSelect}
                    selectedMerchantForDisplay={selectedMerchantForDisplay}
                  />
                )}
                {!isAdmin && selectedMerchantInfo && (
                  <span className="text-sm text-muted-foreground">
                    {selectedMerchantInfo.name} ({selectedMerchantInfo.domain})
                  </span>
                )}
              </div>
            )}
          </div>

          {selectedMerchant && (
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <DateRangePicker
                startDate={dateStart}
                endDate={dateEnd}
                onChange={handleDateRangeChange}
              />
              <Separator orientation="vertical" className="mx-1 hidden h-6 sm:block" />
              {hasData && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={exportToCSV}
                  title={t("analytics.exportCSV")}
                  data-testid="analytics-export-csv"
                >
                  <Download className="size-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedMerchant((prev) => {
                    const tmp = prev
                    setTimeout(() => setSelectedMerchant(tmp), 0)
                    return ""
                  })
                }}
                title="Refresh data"
                data-testid="analytics-refresh"
              >
                <RefreshCw className="size-4" />
              </Button>
            </div>
          )}
        </div>

        {/* ──── States ──── */}
        {!selectedMerchant && isAdmin && (
          <Card className="border-dashed" data-testid="analytics-select-merchant-state">
            <CardContent className="flex min-h-[300px] flex-col items-center justify-center gap-3 py-12">
              <BarChart3 className="size-10 text-muted-foreground/50" />
              <div className="text-center">
                <h3 className="text-lg font-semibold tracking-tight">
                  {t("analytics.selectMerchantToView")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("analytics.selectMerchantDescription")}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {showLoading && selectedMerchant && (
          <Card data-testid="analytics-loading-state">
            <CardContent className="flex min-h-[300px] flex-col items-center justify-center gap-3 py-12">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t("analytics.loadingDescription")}</p>
            </CardContent>
          </Card>
        )}

        {showError && (
          <Card data-testid="analytics-error-state">
            <CardContent className="flex min-h-[300px] flex-col items-center justify-center gap-3 py-12">
              <div className="text-center">
                <h3 className="text-lg font-semibold tracking-tight">
                  {t("analytics.error.title")}
                </h3>
                <p className="text-sm text-muted-foreground">{t("analytics.error.description")}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {showEmpty && selectedMerchant && (
          <Card className="border-dashed" data-testid="analytics-empty-state">
            <CardContent className="flex min-h-[300px] flex-col items-center justify-center gap-3 py-12">
              <BarChart3 className="size-10 text-muted-foreground/50" />
              <div className="text-center">
                <h3 className="text-lg font-semibold tracking-tight">
                  {t("analytics.empty.noData")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("analytics.empty.noDataDescription")}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ──── Analytics dashboard ──── */}
        {selectedMerchant && !showLoading && !showError && !showEmpty && (
          <div className="space-y-6" data-testid="analytics-dashboard">
            {/* Usage Tracking */}
            <UsageChart
              data={usageData}
              threshold={10000}
              currentUsage={totalUsage}
              subscriptionName="Analytics Dashboard"
              startOfCycle={dayjs(dateStart).format("MMM D")}
              endOfCycle={dayjs(dateEnd).format("MMM D")}
              showOverlay={false}
            />

            {/* Key Metrics Summary */}
            <EnhancedAnalyticsSummary
              analyticsItems={widgetAnalytics}
              productAnalytics={productAnalytics}
              startOfCycle={widgetAnalytics[0]?.date || dateStart}
              showOverlay={false}
            />

            {/* Product Distribution + Conversion Funnel */}
            <div className="grid gap-6 lg:grid-cols-2">
              <ProductDistributionChart productAnalytics={productAnalytics} showOverlay={false} />
              <ConversionFunnelChart
                analyticsItems={widgetAnalytics}
                startOfCycle={widgetAnalytics[0]?.date || dateStart}
                showOverlay={false}
              />
            </div>

            {/* Time-based Analysis */}
            <TimeBasedComparison analyticsItems={widgetAnalytics} showOverlay={false} />

            {/* Detailed Metrics Chart */}
            <MetricsChart
              analyticsItems={widgetAnalytics}
              productAnalytics={productAnalytics}
              showOverlay={false}
            />
          </div>
        )}
      </div>
    </div>
  )
}
